import { useMemo } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { computeStoreActivation } from './storeActivationEvidence';
import type { StoreActivationEvidence } from './useStoreActivationEvidence';

export type OnlineOperatingStepId = 'catalog' | 'delivery' | 'mp';

export type OnlineOperatingStep = {
  id: OnlineOperatingStepId;
  label: string;
  hint: string;
  done: boolean;
  optional?: boolean;
};

/**
 * Setup operativo online (tienda ya publicada). Deriva de
 * `useStoreActivationEvidence` (provider) — no fetchea por su cuenta.
 */
export function useOnlineOperatingSetup(userId: string | undefined, evidence: StoreActivationEvidence) {
  const branch = useSessionStore((s) => s.branches.find((b) => b.id === s.currentBranchId));
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const { entitlements } = usePlanEntitlements();
  const mpPlanAllowed = entitlements?.mp_qr_pos === true;

  const loading = evidence.loading;
  const activation = evidence.activation;
  const mpStatus = activation?.mp ?? null;

  const snapshot = useMemo(() => {
    if (!activation) return null;
    const profile = activation.profile;
    const zonesCount = activation.zonesCount ?? 0;
    const onlineCount = activation.onlineVisibleProducts;
    const computed = computeStoreActivation(branch, profile, zonesCount, onlineCount, hasDelivery);
    return { profile, zonesCount, onlineCount, ...computed };
  }, [activation, branch, hasDelivery]);

  // Si la tienda ya está publicada, el catálogo online está done por definición.
  const catalogDone = snapshot?.isPublic === true || snapshot?.onlineDone === true;
  const deliveryDone = snapshot?.deliveryDone ?? false;
  const mpReady = mpStatus?.oauth_connected === true;
  const mpDone = !mpPlanAllowed || mpReady;

  const steps = useMemo((): OnlineOperatingStep[] => {
    const rows: OnlineOperatingStep[] = [
      {
        id: 'catalog',
        label: '1. Catálogo online',
        hint: catalogDone
          ? 'Listo'
          : 'Marcá al menos un producto o servicio como visible online.',
        done: catalogDone,
      },
      {
        id: 'delivery',
        label: '2. Cómo entregan',
        hint: deliveryDone
          ? snapshot?.profile?.deliveryEnabled
            ? 'Listo'
            : 'Solo retiro. Listo.'
          : 'Decí si retiran en el local o si delivery es tuyo. Sin envíos de Vuelto por ahora.',
        done: deliveryDone,
      },
    ];
    if (mpPlanAllowed) {
      rows.push({
        id: 'mp',
        label: '3. Cobro online (MP)',
        hint: mpReady ? 'Listo' : 'Conectá Mercado Pago para cobrar en la web',
        done: mpDone,
        optional: true,
      });
    }
    return rows;
  }, [catalogDone, deliveryDone, mpDone, mpPlanAllowed, mpReady, snapshot?.profile?.deliveryEnabled]);

  const requiredDone = catalogDone && deliveryDone;
  const eligible = Boolean(snapshot?.isPublic);
  const allDone = requiredDone;

  return {
    loading,
    eligible,
    steps,
    allDone,
    requiredDone,
    mpStatus,
    reload: evidence.reload,
  };
}
